import { z } from 'zod';
/**
 * La conversación del paciente con PIA (E4, PIA-2).
 *
 * **PIA responde con el texto aprobado de Fundación, tal cual** (ADR-026):
 * cuando la pregunta encuentra una pieza publicada con confianza suficiente, la
 * respuesta ES el texto de esa pieza, con su código y su versión. Sin modelo
 * generativo en el medio. Si no la encuentra, deriva con un texto fijo; si el
 * mensaje coincide con una frase de la lista de Fundación, sale por la salida
 * segura con otro texto fijo y la conversación se cierra.
 *
 * Los dos textos fijos son los únicos que PIA dice sin que los haya escrito
 * Fundación, y no aconsejan nada sobre la salud: dicen que no hay respuesta, o
 * que hay que llamar al 107. Están PROPUESTOS en `dev/pendientes-fundacion.md`.
 */
/** La confianza mínima para responder con una pieza (ver D-385 de la bitácora). */
export const UMBRAL_RESPUESTA_DIRECTA = 0.8;
/** El largo máximo de un mensaje del paciente. */
export const LARGO_MAXIMO_DEL_MENSAJE = 500;
/** Mensajes por minuto, por programa, antes de responder 429. */
export const MENSAJES_POR_MINUTO = 10;
/** Minutos sin mensajes después de los cuales la conversación se cierra sola. */
export const MINUTOS_DE_INACTIVIDAD_DE_PIA = 30;
/**
 * Los dos textos fijos de PIA. La salida segura es el texto que Fundación validó
 * el 05/08 (copy v2, planilla de semáforos, hoja 5, F20), tal cual; el botón
 * "Llamar al 107" sigue debajo. La derivación sigue PROPUESTA, pendiente de
 * Fundación.
 *
 * La derivación va a nombrar el teléfono del médico de cabecera cuando existan
 * los dos teléfonos de la institución (1.11, pedido de Ezequiel del 27/10):
 * hasta entonces dice "tu equipo de salud".
 */
export const TEXTOS_FIJOS_DE_PIA = Object.freeze({
    /** Versión de los dos textos, para el evento de la salida segura. */
    version: 'v2',
    derivacion: 'Eso no lo puedo responder. Consultalo con tu equipo de salud en tu próximo contacto. Si es urgente, llamá al 107.',
    salidaSegura: 'Lo que me contás podría requerir atención médica. No puedo evaluar si se trata de una urgencia, por lo que te recomiendo comunicarte de inmediato con un servicio de emergencias o acudir al centro de salud más cercano. Si podés, avisale también a un familiar, cuidador o persona de confianza para que pueda ayudarte.',
});
export const nivelDePiaSchema = z.enum([
    'respuesta_directa',
    'derivacion',
    'salida_segura',
    /** Generación anclada (PIA-3): construida y apagada. */
    'respuesta_anclada',
]);
export const cerradaPorSchema = z.enum([
    'paciente',
    'salida_segura',
    'fin_del_programa',
    'inactividad',
]);
export const mensajeDePiaSchema = z.strictObject({
    id: z.uuid(),
    turno: z.number().int().positive(),
    autor: z.enum(['paciente', 'pia']),
    texto: z.string().min(1).max(4000),
    /** Nulo en los mensajes del paciente. */
    nivel: nivelDePiaSchema.nullable(),
    /** El código de la pieza que respondió ("02-003"), para la línea de trazabilidad. */
    piezaCodigo: z
        .string()
        .regex(/^\d{2}-\d{3}$/)
        .nullable(),
    enviadoAt: z.iso.datetime(),
});
/** `GET /api/v1/mi/pia/conversacion`: la abierta o la última cerrada, con sus mensajes. */
export const conversacionDePiaSchema = z.strictObject({
    conversacion: z
        .strictObject({
        id: z.uuid(),
        abierta: z.boolean(),
        cerradaPor: cerradaPorSchema.nullable(),
        mensajes: z.array(mensajeDePiaSchema),
    })
        .nullable(),
    /**
     * PIA busca también en lo que Fundación todavía no aprobó (sólo fuera de
     * producción): la pantalla lo dice con una píldora.
     */
    contenidoEnRevision: z.boolean(),
});
/** `POST /api/v1/mi/pia/mensajes` */
export const mensajeAPiaSchema = z.strictObject({
    texto: z.string().trim().min(1).max(LARGO_MAXIMO_DEL_MENSAJE),
});
/** Lo que vuelve de un mensaje: los dos turnos y si la conversación se cerró. */
export const respuestaDePiaSchema = z.strictObject({
    conversacionId: z.uuid(),
    delPaciente: mensajeDePiaSchema,
    dePia: mensajeDePiaSchema,
    cerrada: z.boolean(),
});
//# sourceMappingURL=conversacion.js.map